import { useEffect, useState } from "react";
import { Eye, EyeOff, MonitorPlay, RotateCcw } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { SubBadge } from "./SectionShell";

const ROWS = [
  ["back_home", "Home link (top left)", "Link back to the website", true],
  ["kicker", "Top label", "Small heading above everything", true],
  ["welcome", "Welcome line", "Use {name} for the participant's name", true],
  ["dayline", "Batch / Day / time line", "Leave text blank for the automatic 'Batch · Day X of Y · time'. Placeholders: {batch} {day} {total}", true],
  ["live_note", "Live talk note", "The highlighted note above the video (also editable in Classroom Videos)", true],
  ["ticker", "Scrolling notice", "One-line scrolling message shown under the heading", true],
  ["day_strip", "Day strip", "Row of Day 1…N chips with dates", false],
  ["video_title", "Video title line", "Placeholders: {title}", true],
  ["no_share", "No-download note", "Small line under the player", true],
  ["live_rules", "Live class rules", "Shown under the player in time-slot modes. Placeholder: {start}", true],
  ["day_rules", "Play-once rules", "Shown under the player in option 2 (day) mode. Placeholder: {until}", true],
  ["video_note", "Per-day video note", "The note saved with each day's video", false],
  ["countdown", "Next-class countdown", "Countdown to the next day's class after the video", false],
  ["tasks_board", "Daily Mandatory Tasks table", "Shown when the video is not playing", false],
  ["footer", "Footer text", "Optional closing line at the bottom", true],
];

const Row = ({ k, label, hint, hasText, value, def, onSave }) => {
  const [text, setText] = useState(value?.text ?? "");
  useEffect(() => setText(value?.text ?? ""), [value?.text]);
  const vis = value?.visible !== false;
  return (
    <div data-testid={`screen-row-${k}`} className={`rounded-xl border px-3 py-2.5 ${vis ? "border-border bg-white" : "border-dashed border-border bg-muted/30"}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          {vis ? <Eye size={14} className="text-emerald-600 shrink-0" /> : <EyeOff size={14} className="text-muted-foreground shrink-0" />}
          <div><p className={`text-sm font-medium ${vis ? "" : "text-muted-foreground line-through"}`}>{label}</p><p className="text-[10px] text-muted-foreground">{hint}</p></div>
        </div>
        <label className="inline-flex items-center gap-2 text-[11px] font-medium" data-testid={`screen-visible-${k}`}>{vis ? "Visible" : "Hidden"}<Switch checked={vis} onCheckedChange={(v) => onSave({ visible: v })} data-testid={`screen-visible-switch-${k}`} /></label>
      </div>
      {hasText && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <input data-testid={`screen-text-${k}`} value={text} onChange={(e) => setText(e.target.value)} placeholder={def?.text || "Text shown to participants"} className="flex-1 min-w-[240px] bg-white border border-border rounded-lg px-3 py-1.5 text-sm outline-none focus:border-accent" />
          <button type="button" data-testid={`screen-save-${k}`} disabled={text === (value?.text ?? "")} onClick={() => onSave({ text })} className="rounded-full bg-[#1A2E26] text-white px-4 py-1.5 text-xs font-medium disabled:opacity-40">Save</button>
          {def && text !== def.text && <button type="button" data-testid={`screen-reset-${k}`} onClick={() => onSave({ text: def.text })} title="Reset to default text" className="inline-flex items-center gap-1 rounded-full border border-border bg-white px-3 py-1.5 text-xs hover:bg-muted"><RotateCcw size={11} /> Default</button>}
        </div>
      )}
    </div>
  );
};

export const ScreenControls = ({ cls }) => {
  const c = cls.classroom || {};
  const screen = c.screen || {};
  const [defaults, setDefaults] = useState(null);
  useEffect(() => { cls.authFetch("/admin/classroom/screen-defaults").then((r) => r.json()).then(setDefaults).catch(() => {}); }, []);
  const save = async (k, patch) => {
    if (k === "live_note" && "text" in patch) await cls.saveField("live_note", patch.text);
    const { text, ...rest } = patch;
    const p = k === "live_note" ? rest : patch;
    if (Object.keys(p).length) await cls.saveField("screen", { [k]: p });
  };
  return (
    <div className="rounded-xl border border-border bg-white/60 p-4 mt-5" data-testid="screen-controls">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <SubBadge />
          <span className="grid place-items-center w-10 h-10 rounded-xl text-white bg-[#1A2E26]"><MonitorPlay size={18} /></span>
          <div>
            <p className="font-serif text-xl font-bold leading-none">Classroom screen — what participants see</p>
            <p className="text-[11px] text-muted-foreground mt-1">Every text and element on the participants' classroom page. Hide / show with the switch, edit the wording and press Save — changes apply within a minute (or on next open). Guides' screen is not affected.</p>
          </div>
        </div>
      </div>
      <div className="mt-3 grid md:grid-cols-2 gap-2">
        {ROWS.map(([k, label, hint, hasText]) => <Row key={k} k={k} label={label} hint={hint} hasText={hasText} value={k === "live_note" ? { ...(screen[k] || {}), text: c.live_note || "" } : screen[k]} def={k === "live_note" ? { text: defaults?.live_note || "" } : defaults?.screen?.[k]} onSave={(p) => save(k, p)} />)}
      </div>
    </div>
  );
};
